import TimelineIcon from '@mui/icons-material/Timeline';
import { Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { formatDate } from 'date-fns';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useDeviceLatestData } from '@/api/devices';
import { SpotterFamily } from '@/common/types';
import { DeviceIcon } from '@/components/DeviceIcon';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { HistoricalSpotterSensorSummary, SpotterSensorSummary } from '@/components/map/SpotterSummaryCard';
import { SpotterContextProvider, useSpotterContext } from '@/context/SpotterContext';
import { useTzContext } from '@/hooks/useTzContext';

export type SpotterInspectorProps = {
    spotterId: string;
    spotterFamily: SpotterFamily;
    /**
     * This bears some explanation:
     *  The inspector can use /fetch/devices or /oceans-api/latest-data as its data source. We should generally match
     * wherever the map layer is getting its data. `latest` matches atlas's ConnectedSpotterLayer, but is limited in time
     * range. `mine` can only work with `devices` and `grid` can only work with `latest`, BUT `shared` can work with either,
     * **iff** we're within a <PublicViewTokenContextProvider>. With `mine`, dataSource is ignored and `devices` is always used.
     * The other two default to `latest`.
     */
    dataSource?: 'devices' | 'latest';
    detailsSearchParams?: URLSearchParams;
    historicalData?: boolean;
};

export const SpotterInspector = ({
    spotterId,
    dataSource,
    spotterFamily,
    detailsSearchParams,
    historicalData,
}: SpotterInspectorProps) => {
    // "mine" can only work from devices, default to latest for the other two
    const viaOceansApi = spotterFamily !== 'mine' && dataSource !== 'devices';

    if (historicalData && viaOceansApi) {
        throw new Error('Historical data inspection via oceans API not supported.');
    }

    return (
        <SpotterContextProvider spotterId={spotterId} viaOceansApi={viaOceansApi} spotterFamily={spotterFamily}>
            <SpotterInspectorContent detailsSearchParams={detailsSearchParams} historicalData={historicalData} />
        </SpotterContextProvider>
    );
};

export const SpotterInspectorContent = ({
    detailsSearchParams,
    historicalData,
}: {
    detailsSearchParams?: URLSearchParams;
    historicalData?: boolean;
}) => {
    const location = useLocation();
    const tzContext = useTzContext();
    const { spotterId, spotterFamily } = useSpotterContext();
    const { device, isPending } = useDeviceLatestData();

    const moreDataHref = useMemo(() => {
        if (!device) {
            return null;
        }

        const searchParams = new URLSearchParams(detailsSearchParams);

        const timestamp = device?.waves?.timestamp ?? device?.track?.[0]?.timestamp;
        if (timestamp) {
            searchParams.set('endDate', formatDate(timestamp, 'yyyy-MM-dd', tzContext));
        }

        const queryString = `?${searchParams.toString()}`;

        switch (spotterFamily) {
            case 'mine':
                return `/spotter/${spotterId}${queryString}#observation-data`;
            case 'grid':
                return `/grid/${spotterId}${queryString}`;
            case 'shared':
                return `/public/${spotterId}${queryString}`;
        }
    }, [detailsSearchParams, device, spotterFamily, spotterId, tzContext]);

    return (
        <Stack sx={{ minWidth: 260 }}>
            <Stack
                direction="row"
                sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
            >
                <Typography variant="h4" sx={{ display: 'flex', gap: 0.5, alignItems: 'center', lineHeight: 1 }}>
                    <DeviceIcon spotterFamily={spotterFamily} hullType={device?.hullType} height="20px" />{' '}
                    {device?.name || spotterId}
                </Typography>
                {moreDataHref && (
                    <Button
                        component={Link}
                        variant="outlined"
                        size="small"
                        sx={{ px: 0.5, py: '1px' }}
                        to={moreDataHref}
                        state={{ backToMapHref: `${location.pathname}${location.search}` }}
                    >
                        <TimelineIcon sx={{ mr: 0.5 }} /> More data
                    </Button>
                )}
            </Stack>

            <LoadingIndicatorIcon isLoading={isPending}>
                <Stack sx={{ width: 1 }}>
                    {device &&
                        (historicalData ? (
                            <HistoricalSpotterSensorSummary
                                sx={INSPECTOR_READOUT_GRID_SX}
                                device={device}
                                dataColumnCount="auto"
                            />
                        ) : (
                            <SpotterSensorSummary
                                sx={INSPECTOR_READOUT_GRID_SX}
                                device={device}
                                dataColumnCount="auto"
                            />
                        ))}
                </Stack>
            </LoadingIndicatorIcon>
        </Stack>
    );
};

/**
 * For use on a DataReadoutGrid with columns="auto":
 * - If there are only a couple data points, size for one generous column
 *   (unless the container is pushed bigger by another child)
 * - If there are lots of data points, size for two columns
 *   (but still small enough to fit inside our max popup width)
 */
export const INSPECTOR_READOUT_GRID_SX: SxProps<Theme> = {
    py: 1,
    width: 1,
    '&:has(>:nth-child(5))': { width: 384 },
};
